import React, { useContext } from 'react';

import { MDText } from 'i18n-react';

import type {
  Document,
} from '../../DocumentCapture.helpers';
import {
  DOCUMENT_CAPTURE_RESPONSE_API_KEYS, DOCUMENT_CAPTURE_ROUTE_NAMES, DOCUMENT_KINDS_VALUES,
} from '../../DocumentCapture.helpers';
import LocalTexts from '../../DocumentCapture.texts.json';
import type { DocumentKindsValue } from '../../DocumentCapture.types';
import { DocumentCaptureConfirmationProgressContext } from '../../DocumentCaptureContexts';
import type { Section } from '../../SummaryTiles/SummaryTiles';
import SummaryTiles from '../../SummaryTiles/SummaryTiles';
import { resolveDocumentTileReactivation } from '../UploadDocuments.helpers';

const LocalT = new MDText(LocalTexts.documentsSummary);

const uploadPagePath = 'upload-documents';

const sortByRequiresAction = (a: Section, b: Section) => {
  if (a.requiresAction && !b.requiresAction) {
    return -1;
  }

  if (!a.requiresAction && b.requiresAction) {
    return 1;
  }

  if (!a.isCompleted && b.isCompleted) {
    return -1;
  }

  if (a.isCompleted && !b.isCompleted) {
    return 1;
  }

  return 0;
};

export const summarySections: Record<
DocumentKindsValue,
{label: React.ReactNode; path: string}
> = {
  additional_documents: {
    label: LocalT.translate('sections.additionalDocument'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_ADDITIONAL_DOCUMENTS,
  },
  bank_statement: {
    label: LocalT.translate('sections.bankStatement'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_BANK_STATEMENT,
  },
  death_certificate: {
    label: LocalT.translate('sections.deathCertificate'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DEATH_CERTIFICATE,
  },
  driving_licence: {
    label: LocalT.translate('sections.drivingLicence'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_DRIVING_LICENCE,
  },
  finance_settlement_letter: {
    label: LocalT.translate('sections.financeSettlement'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_FINANCE_SETTLEMENT_LETTER,
  },
  letter_of_consent: {
    label: LocalT.translate('sections.letterOfConsent'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_LETTER_OF_CONSENT,
  },
  passport: {
    label: LocalT.translate('sections.passport'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_PASSPORT,
  },
  proof_of_purchase: {
    label: LocalT.translate('sections.proofOfPurchase'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_PROOF_OF_PURCHASE,
  },
  solicitors_letter: {
    label: LocalT.translate('sections.solicitorsLetter'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_SOLICITORS_LETTER,
  },
  utility_bill: {
    label: LocalT.translate('sections.utilityBill'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_PROOF_OF_ADDRESS,
  },
  v5: {
    label: LocalT.translate('sections.v5'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_V5,
  },
  will: {
    label: LocalT.translate('sections.will'),
    path: DOCUMENT_CAPTURE_ROUTE_NAMES.UPLOAD_WILL,
  },
};

type DocumentSummaryTilesProps = {
  documentList: DocumentKindsValue[];
}

const DocumentSummaryTiles = ({ documentList }: DocumentSummaryTilesProps) => {
  const { progress: { documents } } = useContext(DocumentCaptureConfirmationProgressContext);
  const submittedKey = DOCUMENT_CAPTURE_RESPONSE_API_KEYS.DOCUMENTS_SUBMITTED;

  // Create new list of documents which adds their uploaded status
  const sections = documentList
    .filter((kind) => DOCUMENT_KINDS_VALUES.includes(kind))
    .map((kind) => {
      const document: Document | undefined = documents?.find((doc) => doc.kind === kind);

      return {
        isCompleted: document?.[submittedKey] ?? false,
        isReactivated: document ? resolveDocumentTileReactivation(document) : false,
        kind,
        label: summarySections[kind].label,
        path: `${uploadPagePath}/${summarySections[kind].path}`,
        requiresAction: document ? resolveDocumentTileReactivation(document) : false,
      };
    }).sort(sortByRequiresAction);

  return (
    <>
      {sections && <SummaryTiles {...{ sections }} />}
    </>
  );
};

export default DocumentSummaryTiles;
